'use client';

import { Avatar, Button, Card, CardContent, Sidebar } from '@lectura/ui';
import {
  Activity,
  BookOpen,
  FolderTree,
  Gauge,
  LayoutDashboard,
  LogOut,
  Plug,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Upload,
  Users,
  Webhook,
} from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import { isIntegrationOnly, useAuth } from '@/lib/auth';
import { AuthGuard } from './AuthGuard';

export function AdminShell({
  children,
  requireAdmin = false,
  requireRoles,
}: {
  children: ReactNode;
  /** Ha igaz, a fő tartalom CSAK admin szerepkörnek jelenik meg; nem-adminnál
   *  „nincs jogosultság" üzenet (a sidebar megmarad). Defense-in-depth a
   *  közvetlen URL-navigáció ellen — a backend amúgy is 403-mal tilt. */
  requireAdmin?: boolean;
  /** Általánosított guard: a fő tartalom akkor jelenik meg, ha a user
   *  szerepkörei közül LEGALÁBB EGY szerepel a listában. A `requireAdmin`
   *  rövidítés a `requireRoles={['admin']}`-nek felel meg; ha mindkettő meg
   *  van adva, a `requireRoles` nyer. */
  requireRoles?: string[];
}) {
  const pathname = usePathname();
  const router = useRouter();
  const user = useAuth((s) => s.user);

  const handleLogout = async () => {
    await useAuth.getState().logout();
    router.replace('/login');
  };

  return (
    <AuthGuard>
      <AdminShellInner
        pathname={pathname}
        user={user}
        onLogout={handleLogout}
        requireRoles={requireRoles ?? (requireAdmin ? ['admin'] : null)}
      >
        {children}
      </AdminShellInner>
    </AuthGuard>
  );
}

function AdminShellInner({
  pathname,
  user,
  onLogout,
  requireRoles,
  children,
}: {
  pathname: string;
  user: ReturnType<typeof useAuth.getState>['user'];
  onLogout: () => void;
  requireRoles: string[] | null;
  children: ReactNode;
}) {

  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname.startsWith(href);

  // RBAC: a librarian szerepkör NEM admin — a backend (require_roles("admin"))
  // a /users, /settings/*, integráció-/admin/*, /webhooks és /audit végpontokat
  // 403-mal tiltja neki. A menüt ehhez igazítjuk, hogy ne lásson olyat, amihez
  // nincs joga (a page-szintű requireRoles guard a közvetlen URL ellen is véd).
  // Az integration-only user (se admin, se librarian) csak az Integrációk
  // oldalt kapja — minden más végpont 403-at adna neki.
  const roles = user?.roles ?? [];
  const isAdmin = roles.includes('admin');
  const integrationOnly = isIntegrationOnly(user);
  const denied = requireRoles !== null && !requireRoles.some((r) => roles.includes(r));

  const integraciokItem = {
    label: 'Integrációk',
    href: '/settings/integrations',
    icon: <Plug />,
    active: isActive('/settings/integrations'),
  };

  return (
    <div className="flex min-h-screen bg-[var(--color-surface-muted)]">
      <Sidebar
        brandName="Lectura"
        brandHref={integrationOnly ? '/settings/integrations' : '/'}
        sections={
          integrationOnly
            ? [{ title: 'Tenant', items: [integraciokItem] }]
            : [
                {
                  items: [
                    { label: 'Áttekintés', href: '/', icon: <LayoutDashboard />, active: isActive('/') && pathname === '/' },
                  ],
                },
                {
                  title: 'Tartalom',
                  items: [
                    { label: 'Publikációk', href: '/publications', icon: <BookOpen />, active: isActive('/publications') },
                    { label: 'Feltöltések', href: '/uploads', icon: <Upload />, active: isActive('/uploads') },
                    { label: 'Kollekciók', href: '/collections', icon: <FolderTree />, active: isActive('/collections') },
                  ],
                },
                {
                  title: 'Műveletek',
                  items: [
                    { label: 'Konverziós sor', href: '/queue', icon: <Activity />, active: isActive('/queue') },
                    ...(isAdmin
                      ? [
                          { label: 'Webhookok', href: '/webhooks', icon: <Webhook />, active: isActive('/webhooks') },
                          { label: 'Audit log', href: '/audit', icon: <ShieldCheck />, active: isActive('/audit') },
                        ]
                      : []),
                  ],
                },
                {
                  title: 'Tenant',
                  items: [
                    ...(isAdmin
                      ? [{ label: 'Felhasználók', href: '/users', icon: <Users />, active: isActive('/users') }]
                      : []),
                    { label: 'Statisztika', href: '/stats', icon: <Gauge />, active: isActive('/stats') },
                    ...(isAdmin
                      ? [
                          integraciokItem,
                          {
                            label: 'Beállítások',
                            href: '/settings',
                            icon: <Settings />,
                            active: pathname === '/settings',
                          },
                        ]
                      : []),
                  ],
                },
              ]
        }
        footer={
          <div className="flex items-center gap-3">
            <Avatar fallback={(user?.display_name ?? '?').slice(0, 2).toUpperCase()} alt={user?.display_name ?? 'felhasználó'} />
            <div className="flex-1 overflow-hidden">
              <p className="truncate text-sm font-semibold">{user?.display_name ?? '—'}</p>
              <p className="truncate text-xs text-[var(--color-foreground-muted)]">{user?.email ?? ''}</p>
            </div>
            <Button variant="ghost" size="icon" aria-label="Kilépés" onClick={onLogout}>
              <LogOut className="size-4" />
            </Button>
          </div>
        }
      />
      <main className="flex-1 overflow-x-hidden">
        <div className="mx-auto max-w-7xl p-8">
          {denied ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 p-12 text-center">
                <ShieldAlert className="size-10 text-[var(--color-foreground-muted)]" />
                <h2 className="font-display text-lg font-semibold">Nincs jogosultság</h2>
                <p className="max-w-md text-sm text-[var(--color-foreground-muted)]">
                  Ehhez az oldalhoz nincs megfelelő szerepköröd. Ha hozzáférésre
                  van szükséged, fordulj a tenant adminisztrátorához.
                </p>
              </CardContent>
            </Card>
          ) : (
            children
          )}
        </div>
      </main>
    </div>
  );
}
